import type { IconName } from "@contracts/IconName";
import type { LocationCategory, LocationTagKind } from "@contracts/socialLocations";

export interface LocationCategoryOption {
  /** A LocationCategory, or "Event" for the tag-kind based option. */
  value: string;
  label: string;
  /** Empty for Other: it shows the plain tone circle with nothing inside. */
  icon: IconName | "";
  tone: string;
}

// Display order for the type dropdowns and pickers (Event is appended last by the dropdown). Keep Other last.
export const LOCATION_CATEGORY_OPTIONS: LocationCategoryOption[] = [
  { value: "FoodDrink", label: "Food & drink", icon: "meal_2", tone: "food" },
  { value: "ShoppingLocalMakers", label: "Shopping", icon: "gift_2", tone: "shopping" },
  { value: "OutdoorsRecreation", label: "Outdoors", icon: "tree_2", tone: "outdoors" },
  { value: "AttractionCulture", label: "Entertainment", icon: "ticket_2", tone: "culture" },
  { value: "Lodging", label: "Lodging", icon: "bed_2", tone: "lodging" },
  { value: "Other", label: "Other", icon: "circle_2", tone: "other" },
];

/** Events are a TagKind, not a category, but they sit in the same icon slot and type filter. */
export const EVENT_CATEGORY_OPTION: LocationCategoryOption = { value: "Event", label: "Event", icon: "calendar_2", tone: "event" };

/**
 * The option to show for a location, or null when there is nothing to show
 * (a Person, or a Location saved before categories were tracked).
 * Nearby suggestions store the AI's name for the catch-all, "OtherVisitorRelevant".
 */
export function categoryOptionFor(tagKind?: LocationTagKind | null, category?: string | null): LocationCategoryOption | null {
  if (tagKind === "Event") return EVENT_CATEGORY_OPTION;
  if (tagKind === "Person" || !category) return null;
  const value = category === "OtherVisitorRelevant" ? "Other" : category;
  return LOCATION_CATEGORY_OPTIONS.find(option => option.value === value) ?? null;
}

// Google place types -> category ============================================
// The (legacy) Places Autocomplete result carries `types`, a list that runs from specific to generic
// (ex. ["brewery", "bar", "restaurant", "food", "point_of_interest", "establishment"]). There is no single
// "primary type" in this API, so we pick the category ourselves.
//
// The rules are checked top to bottom against the WHOLE list, and the first rule that matches any type wins.
// Rule order, not the order Google lists the types in, decides ties, because Google's order is not reliable
// enough to trust on its own. Each position prevents a specific wrong answer:
//  - Lodging first: hotels are routinely also typed restaurant/bar/spa and would otherwise land in Food & drink.
//  - Specific Entertainment before Outdoors and Food: zoos can carry "park", and museums/theaters can have a cafe/bar.
//  - Ski resorts and swimming (pools, water parks) are Entertainment, not Outdoors/Other: they are ticketed venues.
//  - Art studios and sports complexes are Entertainment too: without their own entry they landed in Other.
//  - Outdoors and Food before generic tourist_attraction: parks and breweries keep their primary visitor experience.
//  - Food before Shopping: bakeries and cafes are often also typed "store".
//  - "store"/"food" style generic types are listed in their rule so a specific type we don't know still lands sensibly.
// Anything else (ex. only "point_of_interest") is Other. A missing/empty list returns null so we never
// overwrite with a guess when Google told us nothing.
// NOTE! The backend has a copy of these rules for the category backfill (GooglePlaceTypeCategorizer.cs). Keep them in sync.
const GOOGLE_TYPE_RULES: { category: LocationCategory; types: string[] }[] = [
  { category: "Lodging", types: ["lodging", "hotel", "motel", "campground", "rv_park", "bed_and_breakfast", "resort_hotel", "inn"] },
  {
    category: "AttractionCulture",
    types: [
      "museum",
      "art_gallery",
      "art_studio",
      "amusement_park",
      "amusement_center",
      "video_arcade",
      "indoor_playground",
      "zoo",
      "aquarium",
      "casino",
      "movie_theater",
      "bowling_alley",
      "comedy_club",
      "karaoke",
      "live_music_venue",
      "concert_hall",
      "performing_arts_theater",
      "historical_landmark",
      "cultural_center",
      "library",
      "stadium",
      "sports_complex",
      "church",
      "place_of_worship",
      "visitor_center",
      "city_hall",
      "ski_resort",
      "swimming_pool",
      "water_park",
    ],
  },
  {
    category: "OutdoorsRecreation",
    types: ["park", "national_park", "state_park", "natural_feature", "hiking_area", "marina", "golf_course", "beach", "garden", "botanical_garden", "dog_park"],
  },
  { category: "FoodDrink", types: ["restaurant", "cafe", "bakery", "meal_takeaway", "meal_delivery", "coffee_shop", "ice_cream_shop", "bar", "brewery", "brewpub", "winery", "distillery", "pub", "wine_bar", "food"] },
  { category: "AttractionCulture", types: ["tourist_attraction"] },
  {
    category: "ShoppingLocalMakers",
    types: [
      "shopping_mall",
      "book_store",
      "clothing_store",
      "gift_shop",
      "jewelry_store",
      "shoe_store",
      "furniture_store",
      "home_goods_store",
      "florist",
      "department_store",
      "bicycle_store",
      "liquor_store",
      "market",
      "supermarket",
      "grocery_or_supermarket",
      "convenience_store",
      "store",
    ],
  },
];

export function categoryFromGooglePlaceTypes(types?: string[] | null): LocationCategory | null {
  if (!types?.length) return null;
  const present = new Set(types.map(type => type.toLowerCase()));
  for (const rule of GOOGLE_TYPE_RULES) {
    if (rule.types.some(type => present.has(type))) return rule.category;
  }
  return "Other";
}
